import React, { useState } from 'react';
import { useMutation, useQueryCache } from 'react-query';
import { api } from "../../../api";
import PropTypes from 'prop-types';
import moment from 'moment';
import DeliverooSiteStatusChangeLogs from './DeliverooSiteStatusChangeLogs';

// Pendant de StoreStatusData (Uber Eats) pour un magasin Deliveroo.
// Vocabulaire de leur Site API : OPEN, CLOSED, READY_TO_OPEN (ouvert hors
// horaires : ouvrira au prochain créneau), DISABLED (désactivé chez eux).
const STATUS_LABELS = {
  OPEN: 'OUVERT',
  CLOSED: 'FERMÉ',
  READY_TO_OPEN: "OUVRIRA À L'HEURE D'OUVERTURE",
  DISABLED: 'DÉSACTIVÉ PAR DELIVEROO',
};

// Les raisons qu'ils donnent quand ils refusent d'ouvrir un magasin.
const REFUSAL_REASONS = {
  OPENING_HOURS: "en dehors des heures d'ouverture",
  VACATION_MODE: "le magasin est en mode vacances (à lever dans le Partner Hub ou auprès de Deliveroo)",
  CLOSED_PERIOD: "jour de fermeture, ou trois commandes refusées automatiquement en 15 minutes",
};

const refusalMessage = (error) => {
  const data = error && error.response && error.response.data;
  if (data && data.reason) {
    return `Deliveroo refuse : ${REFUSAL_REASONS[data.reason] || data.reason}.`;
  }
  if (data && data.message) {
    return `Deliveroo refuse : ${data.message}`;
  }
  return error ? error.message : '';
};

const DeliverooSiteStatusData = ({ siteStatusData, centerId, refetch }) => {
  const queryCache = useQueryCache();
  const [isLogsOpen, setIsLogsOpen] = useState(false);
  const [
    handleUpdateSiteStatus,
    { isError: mutateIsError, error: mutateError, isLoading: mutateIsLoading }
  ] = useMutation(({ siteId, status }) => {
    return api.updateDeliverooSiteStatus(centerId, siteId, status);
  }, {
    onSuccess: () => {
      queryCache.invalidateQueries({ queryKey: ['deliverooSiteStatuses', centerId] });
      refetch();
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const status = siteStatusData.status;
  const isOpen = status === 'OPEN' || status === 'READY_TO_OPEN';
  const setStatus = (newStatus) => handleUpdateSiteStatus({ siteId: siteStatusData.site_id, status: newStatus });

  return (
    <>
      <div>
        <h1>{siteStatusData.site_name}</h1>
        <h2>
          {status ? (
            <span className={`badge badge-${isOpen ? 'success' : 'danger'}`}>{STATUS_LABELS[status] || status}</span>
          ) : (
            <span className="badge badge-secondary">STATUT INDISPONIBLE</span>
          )}
          {siteStatusData.on_vacation && (
            <span className="badge badge-warning" style={{ marginLeft: 5 }}>EN VACANCES</span>
          )}
          <button className="btn btn-info btn-sm" style={{ marginLeft: 5 }} onClick={() => setIsLogsOpen(true)}>Voir les changements</button>
        </h2>

        {siteStatusData.on_vacation && (
          <div className="alert alert-warning" role="alert">
            <h4 className="alert-heading">Le magasin est en mode vacances chez Deliveroo.</h4>
            Il ne prend aucune commande, quel que soit son statut.
            {siteStatusData.on_vacation_since && (
              <> Depuis : {moment(siteStatusData.on_vacation_since).locale('fr').format('LLLL')}.</>
            )}<br />
            Ce mode est posé par Deliveroo ou depuis le Partner Hub : il se lève là-bas, pas depuis flow.
          </div>
        )}

        {!status && (
          <div className="alert alert-secondary" role="alert">
            {"Le statut n'a pas pu être lu chez Deliveroo"} ({siteStatusData.transport}).
          </div>
        )}

        {status === 'DISABLED' && (
          <div className="alert alert-danger" role="alert">
            Le magasin est désactivé chez Deliveroo : il ne peut être réactivé que par eux.
          </div>
        )}

        {status === 'CLOSED' && (
          <div className="alert alert-danger" role="alert">
            <h4 className="alert-heading" style={{ backgroundColor: '#f2dede' }}>Le magasin est fermé.</h4>
            Il ne reçoit pas de commandes Deliveroo (pause, horaires, jour de fermeture ou mode vacances).<br />
            Pour le rouvrir, cliquez ci-dessous : en dehors des horaires, il ouvrira au prochain créneau ; si
            Deliveroo refuse, la raison s&apos;affichera.<br />
            <button className="btn btn-success" disabled={mutateIsLoading} onClick={() => setStatus('OPEN')}>Rouvrir le magasin</button>
          </div>
        )}

        {isOpen && (
          <div className="alert alert-success" role="alert">
            <h4 className="alert-heading" style={{ backgroundColor: '#dff0d8' }}>
              {status === 'OPEN' ? 'Le magasin est ouvert.' : "Le magasin ouvrira à sa prochaine heure d'ouverture."}
            </h4>
            <div>
              <button className="btn btn-danger" disabled={mutateIsLoading} onClick={() => setStatus('CLOSED')}>Mettre le magasin en PAUSE</button>
            </div>
          </div>
        )}

        {status && (
          <small>
            Les horaires se règlent <a href={process.env.REACT_APP_DELEEVADMIN + `/admin/slots/byweekdayrule/?center=${centerId}`}
              target="_blank" rel="noopener noreferrer">ici</a> (horaires marketplace) et partent chez Deliveroo automatiquement chaque heure.
          </small>
        )}
        {mutateIsError && <div className="text-danger">{refusalMessage(mutateError)}</div>}
      </div>
      <DeliverooSiteStatusChangeLogs siteId={siteStatusData.site_id} siteName={siteStatusData.site_name} isOpen={isLogsOpen} setIsOpen={setIsLogsOpen} />
      <hr />
    </>
  );
};

DeliverooSiteStatusData.propTypes = {
  siteStatusData: PropTypes.object,
  centerId: PropTypes.number,
  refetch: PropTypes.func,
};

export default DeliverooSiteStatusData;
